'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  GraduationCap, BookOpen, Users, Bot, MessageCircle, Shield,
  Sparkles, ChevronRight, ArrowRight, Building2, Lock, LogIn,
  ExternalLink, Calendar, BarChart3, Phone, Zap, CheckCircle2,
  Menu, X, ChevronDown, ArrowUp, UserPlus,
} from 'lucide-react';

// ===================== NAVBAR =====================
function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const scrollTo = (id: string) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-teal-500/20">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight text-gray-900">智能教务助理</h1>
              <p className="text-xs text-gray-400">EduAssistant SaaS</p>
            </div>
          </div>
          <div className="hidden lg:flex items-center gap-1">
            {[
              { label: '产品功能', id: 'features' },
              { label: '演示体验', id: 'demo' },
              { label: '适用场景', id: 'scenarios' },
              { label: '核心优势', id: 'advantages' },
            ].map(item => (
              <button key={item.id} onClick={() => scrollTo(item.id)} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-teal-600 hover:bg-teal-50 transition-all duration-200">
                {item.label}
              </button>
            ))}
            <Button onClick={() => document.getElementById('login')?.scrollIntoView({ behavior: 'smooth' })} className="ml-3 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white shadow-lg shadow-teal-500/25 rounded-full px-6">
              登录 / 注册
            </Button>
          </div>
          <button className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-6 h-6 text-gray-700" /> : <Menu className="w-6 h-6 text-gray-700" />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-xl">
          <div className="px-4 py-4 space-y-1">
            {[
              { label: '产品功能', id: 'features' },
              { label: '演示体验', id: 'demo' },
              { label: '适用场景', id: 'scenarios' },
              { label: '核心优势', id: 'advantages' },
            ].map(item => (
              <button key={item.id} onClick={() => scrollTo(item.id)} className="block w-full text-left px-4 py-3 rounded-lg text-gray-700 hover:bg-teal-50 hover:text-teal-600 font-medium transition-colors">
                {item.label}
              </button>
            ))}
            <div className="pt-3">
              <Button onClick={() => document.getElementById('login')?.scrollIntoView({ behavior: 'smooth' })} className="w-full bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-full">
                登录 / 注册
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

// ===================== HERO =====================
function HeroSection() {
  return (
    <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-teal-50/30 to-emerald-50/20" />
      <div className="absolute top-20 right-1/4 w-96 h-96 bg-teal-100/40 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-emerald-100/30 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100 mb-6 px-4 py-1.5 text-sm">
            <Zap className="w-3.5 h-3.5 mr-1.5" />
            AI驱动的教育培训SaaS平台
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
            让每一家培训机构
            <br />
            <span className="bg-gradient-to-r from-teal-500 to-emerald-600 bg-clip-text text-transparent">
              都拥有智能教务系统
            </span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed">
            一站式教务管理 + AI智能客服 + 机构专属官网，开箱即用，无需技术团队，助力教育培训机构数字化升级
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => { document.getElementById('login')?.scrollIntoView({ behavior: 'smooth' }); window.location.hash = 'register'; }}
              className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white shadow-xl shadow-teal-500/30 rounded-full px-8 h-14 text-base"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              免费注册
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => { document.getElementById('login')?.scrollIntoView({ behavior: 'smooth' }); window.location.hash = ''; }}
              className="border-gray-200 text-gray-700 hover:bg-gray-50 rounded-full px-8 h-14 text-base"
            >
              <LogIn className="w-5 h-5 mr-2" />
              已有账号登录
            </Button>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              { icon: <Building2 className="w-5 h-5" />, value: '多机构', label: '独立租户隔离' },
              { icon: <Bot className="w-5 h-5" />, value: 'AI', label: '智能客服对话' },
              { icon: <GraduationCap className="w-5 h-5" />, value: '全流程', label: '教务排课管理' },
              { icon: <Globe className="w-5 h-5" />, value: '专属', label: '独立品牌官网' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-white/70 backdrop-blur-sm border border-gray-100 shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center text-white flex-shrink-0">
                  {item.icon}
                </div>
                <div className="text-left">
                  <div className="text-base font-bold text-gray-900">{item.value}</div>
                  <div className="text-xs text-gray-400">{item.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ===================== LOGIN / REGISTER SECTION =====================
function LoginRegisterSection() {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');

  // Listen for hash change to switch tab (from Hero CTA buttons)
  React.useEffect(() => {
    const checkHash = () => {
      if (window.location.hash === '#register') {
        setActiveTab('register');
      }
    };
    checkHash();
    window.addEventListener('hashchange', checkHash);
    return () => window.removeEventListener('hashchange', checkHash);
  }, []);

  // Login state
  const [loginSlug, setLoginSlug] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Register state
  const [regName, setRegName] = useState('');
  const [regSlug, setRegSlug] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPassword2, setRegPassword2] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');
  const [regSuccess, setRegSuccess] = useState('');

  // View site
  const [viewSlug, setViewSlug] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    if (!loginSlug.trim() || !loginPassword.trim()) {
      setLoginError('请输入机构标识和管理密码');
      return;
    }
    setLoginLoading(true);
    try {
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: loginSlug.trim(), password: loginPassword }),
      });
      const data = await res.json();
      if (res.ok && data.tenant) {
        localStorage.setItem(`admin_auth_${loginSlug.trim()}`, JSON.stringify({ password: loginPassword, tenant: data.tenant }));
        router.push(`/admin/${loginSlug.trim()}`);
      } else {
        setLoginError(data.error || '登录失败');
      }
    } catch {
      setLoginError('网络错误，请重试');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegError('');
    setRegSuccess('');
    if (!regName.trim() || !regSlug.trim() || !regPhone.trim() || !regPassword) {
      setRegError('请填写所有必填项');
      return;
    }
    if (regPassword !== regPassword2) {
      setRegError('两次输入的密码不一致');
      return;
    }
    if (regPassword.length < 6) {
      setRegError('密码至少6位');
      return;
    }
    if (!/^1[3-9]\d{9}$/.test(regPhone.trim())) {
      setRegError('手机号格式不正确');
      return;
    }
    setRegLoading(true);
    try {
      const res = await fetch('/api/admin/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: regName.trim(), slug: regSlug.trim(), password: regPassword, phone: regPhone.trim() }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRegSuccess(data.message);
        // Registration now requires approval - do NOT auto-login
        setRegName(''); setRegSlug(''); setRegPhone(''); setRegPassword(''); setRegPassword2('');
      } else {
        setRegError(data.error || '注册失败');
      }
    } catch {
      setRegError('网络错误，请重试');
    } finally {
      setRegLoading(false);
    }
  };

  const handleViewSite = () => {
    if (!viewSlug.trim()) return;
    router.push(`/t/${viewSlug.trim()}`);
  };

  // Spinning loader component
  const Spinner = () => <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />;

  return (
    <section id="login" className="py-20 lg:py-28 bg-white relative">
      <div className="absolute top-0 right-0 w-72 h-72 bg-teal-50 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-50 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100 mb-4 px-4 py-1">
            {activeTab === 'login' ? <LogIn className="w-3.5 h-3.5 mr-1.5" /> : <UserPlus className="w-3.5 h-3.5 mr-1.5" />}
            机构入口
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            {activeTab === 'login' ? '机构管理员' : '新机构'}<span className="text-teal-600">{activeTab === 'login' ? '登录' : '注册'}</span>
          </h2>
          <p className="mt-4 text-gray-500 text-lg">
            {activeTab === 'login' ? '输入机构标识和密码，进入管理后台' : '填写基本信息，提交注册申请（需审核）'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 max-w-5xl mx-auto">
          {/* Main Card */}
          <Card className="lg:col-span-3 border-0 shadow-xl hover:shadow-2xl transition-shadow">
            <CardContent className="p-6 lg:p-8">
              {/* Tab Switcher */}
              <div className="flex mb-6 bg-gray-100 rounded-xl p-1">
                <button
                  onClick={() => { setActiveTab('login'); setRegError(''); setRegSuccess(''); }}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${activeTab === 'login' ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <LogIn className="w-4 h-4" />
                  登录已有账号
                </button>
                <button
                  onClick={() => { setActiveTab('register'); setLoginError(''); }}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${activeTab === 'register' ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <UserPlus className="w-4 h-4" />
                  注册新机构
                </button>
              </div>

              {/* Login Form */}
              {activeTab === 'login' && (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">机构标识</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="输入您的机构标识，如 demo"
                        value={loginSlug}
                        onChange={e => setLoginSlug(e.target.value.toLowerCase())}
                        className="pl-10 h-12 rounded-xl"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">管理密码</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="password"
                        placeholder="请输入管理密码"
                        value={loginPassword}
                        onChange={e => setLoginPassword(e.target.value)}
                        className="pl-10 h-12 rounded-xl"
                      />
                    </div>
                  </div>
                  {loginError && <div className="text-sm text-red-500 bg-red-50 rounded-lg px-4 py-2">{loginError}</div>}
                  <Button
                    type="submit"
                    disabled={loginLoading}
                    className="w-full h-12 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white rounded-xl text-base font-semibold shadow-lg shadow-teal-500/25"
                  >
                    {loginLoading ? <span className="flex items-center gap-2"><Spinner /> 登录中...</span> : <><LogIn className="w-4 h-4 mr-2" />登录管理后台</>}
                  </Button>
                </form>
              )}

              {/* Register Form */}
              {activeTab === 'register' && (
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">机构名称 <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="如：新启程教育"
                        value={regName}
                        onChange={e => setRegName(e.target.value)}
                        className="pl-10 h-12 rounded-xl"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">机构标识 (slug) <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-mono">/t/</span>
                      <Input
                        placeholder="如：xinqicheng（小写字母、数字、连字符）"
                        value={regSlug}
                        onChange={e => setRegSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                        className="pl-12 h-12 rounded-xl font-mono"
                      />
                    </div>
                    <p className="text-xs text-gray-400">注册成功后，您的官网地址为：edu.aitokenhub.top/t/<strong>{regSlug || 'xxx'}</strong></p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">联系手机号 <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="请输入手机号码"
                        value={regPhone}
                        onChange={e => setRegPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                        className="pl-10 h-12 rounded-xl"
                        maxLength={11}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">设置密码 <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="password"
                          placeholder="至少6位"
                          value={regPassword}
                          onChange={e => setRegPassword(e.target.value)}
                          className="pl-10 h-12 rounded-xl"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">确认密码 <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="password"
                          placeholder="再次输入密码"
                          value={regPassword2}
                          onChange={e => setRegPassword2(e.target.value)}
                          className="pl-10 h-12 rounded-xl"
                        />
                      </div>
                    </div>
                  </div>
                  {regError && <div className="text-sm text-red-500 bg-red-50 rounded-lg px-4 py-2">{regError}</div>}
                  {regSuccess && <div className="text-sm text-teal-600 bg-teal-50 rounded-lg px-4 py-2">{regSuccess}</div>}
                  <Button
                    type="submit"
                    disabled={regLoading || !!regSuccess}
                    className="w-full h-12 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white rounded-xl text-base font-semibold shadow-lg shadow-teal-500/25"
                  >
                    {regLoading ? <span className="flex items-center gap-2"><Spinner /> 提交中...</span> : <><UserPlus className="w-4 h-4 mr-2" />提交注册申请</>}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Sidebar */}
          <div className="lg:col-span-2 space-y-4">
            {/* View Site Card */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white">
                    <ExternalLink className="w-5 h-5" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900">预览机构官网</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">输入机构标识，查看该机构的官方网站</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="输入slug，如 demo"
                    value={viewSlug}
                    onChange={e => setViewSlug(e.target.value.toLowerCase())}
                    onKeyDown={e => { if (e.key === 'Enter') handleViewSite() }}
                    className="h-10 rounded-lg"
                  />
                  <Button variant="outline" onClick={handleViewSite} className="h-10 rounded-lg shrink-0">
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* What you get */}
            <Card className="border-0 shadow-md bg-gradient-to-br from-gray-50 to-teal-50/30">
              <CardContent className="p-6 space-y-3">
                <h4 className="font-semibold text-gray-900 text-sm">注册即享</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-teal-500 shrink-0" />
                    <span>独立品牌官网（含默认模板）</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-teal-500 shrink-0" />
                    <span>完整教务管理系统</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-teal-500 shrink-0" />
                    <span>AI智能客服"小教助"</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-teal-500 shrink-0" />
                    <span>学生/教师/排课管理</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-teal-500 shrink-0" />
                    <span>注册后需等待管理员审核</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}

// ===================== FEATURES =====================
function FeaturesSection() {
  const features = [
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: '官网定制',
      desc: '每个机构拥有独立品牌官网，课程、师资、案例等板块自由编辑，支持SEO优化，打造专业品牌形象',
      color: 'from-teal-500 to-emerald-500',
      shadow: 'shadow-teal-500/20',
    },
    {
      icon: <Bot className="w-6 h-6" />,
      title: 'AI智能客服',
      desc: '基于大语言模型的智能教务助理"小教助"，自动回答学员关于课程、课时、排课等问题，7x24小时在线服务',
      color: 'from-violet-500 to-purple-500',
      shadow: 'shadow-violet-500/20',
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: '智能排课',
      desc: '灵活的排课系统，支持多校区、多教室、多教师协同管理，冲突检测自动提醒，让教务管理井井有条',
      color: 'from-amber-500 to-orange-500',
      shadow: 'shadow-amber-500/20',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: '学员管理',
      desc: '全面的学员档案管理，课时余额实时追踪，请假、补课流程化处理，学员端自助查询，减少沟通成本',
      color: 'from-pink-500 to-rose-500',
      shadow: 'shadow-pink-500/20',
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: '课程管理',
      desc: '多课程类型支持，价格体系灵活配置，开班、停课一键操作，课时消耗自动记录，教学数据一目了然',
      color: 'from-blue-500 to-indigo-500',
      shadow: 'shadow-blue-500/20',
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: '数据看板',
      desc: '招生数据、课时消耗、教师工作量等多维度数据分析，帮助机构管理者做出更精准的经营决策',
      color: 'from-cyan-500 to-teal-500',
      shadow: 'shadow-cyan-500/20',
    },
  ];

  return (
    <section id="features" className="py-20 lg:py-28 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100 mb-4 px-4 py-1">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            产品功能
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            一站式教务管理<span className="text-teal-600">解决方案</span>
          </h2>
          <p className="mt-4 text-gray-500 text-lg">
            从招生到教学、从排课到家校沟通，覆盖教培机构全业务场景
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((f, i) => (
            <div key={i} className="group p-6 lg:p-8 rounded-2xl bg-white border border-gray-100 hover:border-teal-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center text-white shadow-lg ${f.shadow} group-hover:scale-110 transition-transform`}>
                {f.icon}
              </div>
              <h3 className="mt-5 text-lg font-bold text-gray-900">{f.title}</h3>
              <p className="mt-2 text-gray-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===================== SCENARIOS =====================
function ScenariosSection() {
  return (
    <section id="scenarios" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100 mb-4 px-4 py-1">
            <Users className="w-3.5 h-3.5 mr-1.5" />
            适用场景
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            广泛适用于<span className="text-teal-600">各类教育机构</span>
          </h2>
          <p className="mt-4 text-gray-500 text-lg">无论是学科辅导、艺术培训还是素质教育，都能完美适配</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: <BookOpen className="w-6 h-6" />, title: 'K12学科辅导', desc: '数学、英语、语文等学科类培训机构', tag: '热门' },
            { icon: <Sparkles className="w-6 h-6" />, title: '艺术教育', desc: '钢琴、绘画、舞蹈、书法等艺术培训', tag: '' },
            { icon: <GraduationCap className="w-6 h-6" />, title: '素质教育', desc: '编程、STEAM、口才、体能等素质教育', tag: '' },
            { icon: <Zap className="w-6 h-6" />, title: '托管中心', desc: '课后托管、晚辅导、周末托管等服务机构', tag: '' },
          ].map((item, i) => (
            <Card key={i} className="border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-teal-500 to-emerald-500" />
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600">
                    {item.icon}
                  </div>
                  {item.tag && <Badge className="bg-red-500 text-white text-xs">{item.tag}</Badge>}
                </div>
                <h3 className="text-base font-bold text-gray-900">{item.title}</h3>
                <p className="mt-2 text-sm text-gray-500">{item.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===================== ADVANTAGES =====================
function AdvantagesSection() {
  const advantages = [
    { icon: <Shield className="w-6 h-6" />, title: '数据隔离', desc: '多租户架构确保每个机构的数据完全独立，互不可见，安全可靠' },
    { icon: <Zap className="w-6 h-6" />, title: '开箱即用', desc: '注册即用，无需自建服务器和技术团队，大幅降低运营成本' },
    { icon: <MessageCircle className="w-6 h-6" />, title: 'AI赋能', desc: '内置大语言模型智能客服，自动回答常见问题，减轻教务老师负担' },
    { icon: <Phone className="w-6 h-6" />, title: '微信生态', desc: '对接微信公众号，学员在微信内即可完成约课、查课时、请假等操作' },
    { icon: <BarChart3 className="w-6 h-6" />, title: '数据驱动', desc: '经营数据可视化看板，招生转化、课时消耗、满班率实时掌握' },
    { icon: <Users className="w-6 h-6" />, title: '学员互联', desc: '学员端实时同步排课、请假、课时等信息，减少沟通成本，提升满意度' },
  ];

  return (
    <section id="advantages" className="py-20 lg:py-28 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Badge className="bg-teal-500/20 text-teal-400 hover:bg-teal-500/20 mb-4 px-4 py-1 border-teal-500/30">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            为什么选择我们
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold">
            六大核心<span className="text-teal-400">优势</span>
          </h2>
          <p className="mt-4 text-gray-400 text-lg">
            专注教育培训行业，深入理解机构痛点
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {advantages.map((a, i) => (
            <div key={i} className="group p-6 lg:p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-teal-500/30 hover:bg-white/10 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center text-white shadow-lg shadow-teal-500/20 group-hover:scale-110 transition-transform">
                {a.icon}
              </div>
              <h3 className="mt-5 text-lg font-bold">{a.title}</h3>
              <p className="mt-2 text-gray-400 leading-relaxed">{a.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===================== DEMO SECTION (模拟对话演示) =====================
function DemoSection() {
  const [activeStep, setActiveStep] = useState(0);
  const [autoPlaying, setAutoPlaying] = useState(false);

  const steps = [
    {
      user: '我想给我家小明约一节数学课',
      ai: '好的，我来帮您查询一下小明的课时情况。\n\n小明目前剩余 18 课时，本周可约的数学课有：\n\n周三 15:00-16:00 张老师 数学思维班（剩余3个名额）\n周六 10:00-11:00 王老师 数学提升班（剩余5个名额）\n\n请问您方便哪个时段？',
      label: '学员发起约课',
    },
    {
      user: '周三下午吧，就张老师的课',
      ai: '已为小明预约成功！\n\n课程：数学思维班\n教师：张老师\n时间：本周三 15:00-16:00\n教室：A201\n\n已扣除 1 课时，小明剩余 17 课时。\n\n已向张老师发送通知，课前30分钟会提醒您准时到课。',
      label: 'AI自动安排并确认',
    },
    {
      user: '小明同学想试听一下英语课，可以吗？',
      ai: '当然可以！试听课不扣课时。\n\n本周英语课安排：\n\n周四 14:00-15:00 李老师 英语启蒙班\n周五 16:00-17:00 李老师 英语口语班\n\n请选择试听时段，我帮小明安排。',
      label: 'AI灵活响应试听需求',
    },
    {
      user: '小明这周三有点不舒服，想请个假',
      ai: '已收到请假申请，已通知张老师。\n\n请假详情：\n学生：小明\n课程：数学思维班\n时间：周三 15:00-16:00\n\n本次请假课时已冻结（不扣除），可后续补课。\n\n祝小明早日康复！',
      label: 'AI处理请假自动通知',
    },
  ];

  const autoPlayTimer = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const startAutoPlay = () => {
    setAutoPlaying(true);
    setActiveStep(0);
  };

  const stopAutoPlay = () => {
    setAutoPlaying(false);
    if (autoPlayTimer.current) clearInterval(autoPlayTimer.current);
  };

  React.useEffect(() => {
    if (autoPlaying) {
      autoPlayTimer.current = setInterval(() => {
        setActiveStep(prev => {
          if (prev >= steps.length - 1) {
            stopAutoPlay();
            return prev;
          }
          return prev + 1;
        });
      }, 3500);
    }
    return () => { if (autoPlayTimer.current) clearInterval(autoPlayTimer.current); };
  }, [autoPlaying]);

  const currentStep = steps[activeStep];

  return (
    <section id="demo" className="py-20 lg:py-28 bg-gradient-to-br from-gray-50 via-teal-50/20 to-emerald-50/10 relative overflow-hidden">
      <div className="absolute top-20 left-10 w-72 h-72 bg-teal-100/30 rounded-full blur-3xl" />
      <div className="absolute bottom-10 right-10 w-64 h-64 bg-emerald-100/30 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100 mb-4 px-4 py-1">
            <Bot className="w-3.5 h-3.5 mr-1.5" />
            产品演示
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            学员微信<span className="text-teal-600">AI约课</span>体验
          </h2>
          <p className="mt-4 text-gray-500 text-lg">
            学员在公众号对话中即可完成约课、试听、请假，AI小教助秒级响应
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Phone mockup */}
          <div className="flex justify-center">
            <div className="w-full max-w-sm">
              {/* Phone frame */}
              <div className="bg-gray-900 rounded-[2.5rem] p-3 shadow-2xl">
                <div className="bg-white rounded-[2rem] overflow-hidden">
                  {/* Status bar */}
                  <div className="bg-gradient-to-r from-green-500 to-green-600 px-5 pt-3 pb-2 flex items-center justify-between">
                    <span className="text-white text-xs font-medium">9:41</span>
                    <div className="flex items-center gap-1">
                      <div className="w-1 h-1 bg-white rounded-full" />
                      <div className="w-1 h-1 bg-white rounded-full" />
                      <div className="w-1 h-1 bg-white rounded-full" />
                    </div>
                  </div>
                  {/* Chat header */}
                  <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="text-white text-sm font-semibold">新启程教育小教助</div>
                      <div className="text-green-100 text-[10px]">在线</div>
                    </div>
                  </div>
                  {/* Chat area */}
                  <div className="bg-gray-50 p-4 min-h-[420px] max-h-[420px] overflow-y-auto space-y-3" key={activeStep}>
                    {/* System message */}
                    <div className="text-center">
                      <span className="text-[10px] text-gray-400 bg-gray-200/60 px-2 py-0.5 rounded-full">AI智能教务助理已接入</span>
                    </div>
                    {/* Render messages up to current step */}
                    {steps.slice(0, activeStep + 1).map((step, i) => (
                      <React.Fragment key={i}>
                        {/* User message */}
                        <div className="flex justify-end">
                          <div className="bg-green-500 text-white text-sm px-3.5 py-2.5 rounded-2xl rounded-tr-md max-w-[75%] shadow-sm">
                            {step.user}
                          </div>
                        </div>
                        {/* AI reply */}
                        <div className="flex justify-start">
                          <div className="bg-white text-gray-700 text-sm px-3.5 py-2.5 rounded-2xl rounded-tl-md max-w-[80%] shadow-sm border border-gray-100">
                            <div className="whitespace-pre-line leading-relaxed">{step.ai}</div>
                          </div>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
              {/* Step indicator */}
              <div className="flex justify-center gap-1.5 mt-6">
                {steps.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { stopAutoPlay(); setActiveStep(i); }}
                    className={`h-2 rounded-full transition-all duration-300 ${i === activeStep ? 'w-8 bg-teal-500' : 'w-2 bg-gray-300 hover:bg-gray-400'}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right: Step descriptions */}
          <div className="space-y-4">
            {!autoPlaying && (
              <div className="text-center lg:text-left mb-6">
                <Button onClick={startAutoPlay} className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white shadow-lg shadow-teal-500/25 rounded-full px-8">
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                  自动播放演示
                </Button>
              </div>
            )}
            {steps.map((step, i) => (
              <div
                key={i}
                onClick={() => { stopAutoPlay(); setActiveStep(i); }}
                className={`p-5 rounded-2xl border-2 transition-all duration-300 cursor-pointer ${
                  i === activeStep
                    ? 'bg-white border-teal-300 shadow-lg shadow-teal-500/10 scale-[1.02]'
                    : i < activeStep
                    ? 'bg-white/50 border-gray-200 opacity-60'
                    : 'bg-white/80 border-gray-100 hover:border-teal-200 hover:shadow-sm'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    i === activeStep
                      ? 'bg-gradient-to-br from-teal-500 to-emerald-500 text-white shadow-lg shadow-teal-500/20'
                      : i < activeStep
                      ? 'bg-teal-100 text-teal-500'
                      : 'bg-gray-100 text-gray-400'
                  }`}>
                    {i < activeStep ? <CheckCircle2 className="w-5 h-5" /> : <span className="text-sm font-bold">{i + 1}</span>}
                  </div>
                  <div className="min-w-0">
                    <div className={`text-sm font-bold mb-1 ${i === activeStep ? 'text-teal-700' : 'text-gray-700'}`}>
                      {step.label}
                    </div>
                    <div className={`text-sm ${i === activeStep ? 'text-gray-600' : 'text-gray-400'}`}>
                      {step.user.slice(0, 20)}...
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ===================== CTA =====================
function CTASection() {
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
          准备好开始了吗？
        </h2>
        <p className="mt-4 text-gray-500 text-lg">
          立即登录，体验智能教务管理带来的效率提升
        </p>
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Button
            size="lg"
            onClick={() => { document.getElementById('login')?.scrollIntoView({ behavior: 'smooth' }); window.location.hash = 'register'; }}
            className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white shadow-xl shadow-teal-500/30 rounded-full px-8 h-14 text-base"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            免费注册，立即体验
          </Button>
        </div>
      </div>
    </section>
  );
}

// ===================== FOOTER =====================
function Footer({ onShowPrivacy, onShowAgreement }: { onShowPrivacy: () => void; onShowAgreement: () => void }) {
  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold">智能教务助理 SaaS 平台</span>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <button onClick={onShowPrivacy} className="hover:text-teal-400 transition-colors">隐私政策</button>
            <span>&middot;</span>
            <button onClick={onShowAgreement} className="hover:text-teal-400 transition-colors">用户协议</button>
            <span>&middot;</span>
            <span>&copy; 2026 版权所有</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ===================== BACK TO TOP =====================
function BackToTop() {
  const [show, setShow] = useState(false);
  React.useEffect(() => {
    const h = () => setShow(window.scrollY > 500);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);
  if (!show) return null;
  return (
    <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="fixed bottom-8 right-8 w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 text-white shadow-xl shadow-teal-500/30 flex items-center justify-center hover:scale-110 transition-transform z-40" aria-label="回到顶部">
      <ArrowUp className="w-5 h-5" />
    </button>
  );
}

// ===================== Globe icon (inline) =====================
function Globe({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
      <path d="M2 12h20" />
    </svg>
  );
}

// ===================== POLICY MODAL =====================
function PolicyModal({ isOpen, onClose, title, content }: { isOpen: boolean; onClose: () => void; title: string; content: React.ReactNode }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"><X className="w-4 h-4 text-gray-500" /></button>
        </div>
        <div className="overflow-y-auto px-6 py-5 text-sm text-gray-700 leading-relaxed space-y-4">{content}</div>
        <div className="px-6 py-4 border-t border-gray-100 shrink-0">
          <button onClick={onClose} className="w-full py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium">我已阅读</button>
        </div>
      </div>
    </div>
  );
}

const SAAS_PRIVACY_CONTENT = (
  <>
    <p className="text-xs text-gray-400">最后更新日期：2026年7月</p>

    <h3 className="font-semibold text-gray-900 mt-4">一、信息收集</h3>
    <p>本平台（智能教务助理SaaS）由吱唔智能科技运营，在提供服务过程中会收集以下信息：</p>
    <p><strong>机构信息</strong>：机构名称、联系人、手机号、邮箱等注册信息。</p>
    <p><strong>学员信息</strong>：姓名、年级、联系电话等（由机构管理员录入，机构负责取得监护人同意）。</p>
    <p><strong>教师信息</strong>：姓名、科目、联系方式等。</p>
    <p><strong>系统日志</strong>：设备信息、IP地址、操作日志等。</p>

    <h3 className="font-semibold text-gray-900 mt-4">二、信息使用</h3>
    <p>收集的信息用于：提供教务管理、排课、约课、家校沟通等核心功能；提供AI智能客服辅助服务；进行安全监控和风险防范；发送服务通知和提醒；遵守法律法规要求。</p>

    <h3 className="font-semibold text-gray-900 mt-4">三、信息存储与保护</h3>
    <p>平台采用SSL/TLS加密传输、数据库加密存储、访问权限控制等安全技术措施。服务器部署在中国境内认证数据中心。多租户架构确保各机构数据完全隔离。平台会定期进行安全审计，确保数据安全。</p>

    <h3 className="font-semibold text-gray-900 mt-4">四、信息共享</h3>
    <p>平台承诺不会向第三方出售、出租或交易用户个人信息。仅在以下情况下可能共享：获得明确同意；法律法规或行政/司法机构要求；为保护平台或其他用户合法权益所必需。</p>

    <h3 className="font-semibold text-gray-900 mt-4">五、用户权利</h3>
    <p>用户有权查询、更正、删除其个人信息。机构注销后，平台将在合理期限内删除相关数据。平台提醒机构在收集学员信息时应依法取得监护人同意。</p>

    <h3 className="font-semibold text-gray-900 mt-4">六、政策更新</h3>
    <p>我们可能不时更新本隐私政策。重大变更时，会通过平台内通知等方式告知。继续使用服务即表示同意更新后的政策。</p>

    <h3 className="font-semibold text-gray-900 mt-4">七、联系我们</h3>
    <p>如有任何疑问、意见或投诉，请联系我们：<br/>邮箱：admin@zhivoo.cn</p>
  </>
);

const SAAS_AGREEMENT_CONTENT = (
  <>
    <p className="text-xs text-gray-400">最后更新日期：2026年5月31日</p>
    <h3 className="font-semibold text-gray-900">一、服务说明</h3>
    <p>本平台（智能教务助理SaaS）由吱唔智能科技提供，旨在为教育培训机构提供学员管理、课程管理、排课管理、AI智能客服等综合教务管理服务。平台有权根据需要修改本协议条款。</p>
    <h3 className="font-semibold text-gray-900">二、账号管理</h3>
    <p>机构注册时应提供真实准确的资料信息，妥善保管账号密码，不得转让出借给第三方。机构上传的学员、教师、课程等业务数据所有权归机构所有。</p>
    <h3 className="font-semibold text-gray-900">三、行为规范</h3>
    <p>用户应遵守法律法规，不得利用平台从事违法违规活动，不得发布传播违法违规内容，不得干扰平台正常运行。</p>
    <h3 className="font-semibold text-gray-900">四、服务变更与终止</h3>
    <p>平台有权在提前通知的情况下变更、升级或暂停服务。用户严重违反协议的，平台有权终止服务。服务终止后将在合理期限内保留数据。</p>
    <h3 className="font-semibold text-gray-900">五、免责声明</h3>
    <p>平台服务按"现状"提供，对于因不可抗力导致的服务中断或数据损失不承担责任。</p>
    <h3 className="font-semibold text-gray-900">六、争议解决</h3>
    <p>本协议适用中华人民共和国法律，争议应首先协商解决，协商不成可向有管辖权的人民法院提起诉讼。</p>
  </>
);

// ===================== MAIN =====================
export default function Home() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);

  return (
    <div className="min-h-screen">
      <Navbar />
      <HeroSection />
      <LoginRegisterSection />
      <DemoSection />
      <FeaturesSection />
      <ScenariosSection />
      <AdvantagesSection />
      <CTASection />
      <Footer onShowPrivacy={() => setShowPrivacy(true)} onShowAgreement={() => setShowAgreement(true)} />
      <BackToTop />
      <PolicyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="隐私政策" content={SAAS_PRIVACY_CONTENT} />
      <PolicyModal isOpen={showAgreement} onClose={() => setShowAgreement(false)} title="用户协议" content={SAAS_AGREEMENT_CONTENT} />
    </div>
  );
}
